import { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './ParishBookCategoryTags.module.scss';

import TagsInput from 'Components/Common/TagsInput/TagsInput';
import { InputElement } from 'Components/Common/TagsInput/types';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import EmptyState from 'Components/Common/EmptyState/EmptyState';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetUserPermission from 'hooks/useGetUserPermission';
import { CONTACT_ROLE_MAX_LENGTH } from 'utils/validationUtils';
import {
    ParishBookCategoryDto,
    useCreateParishBookCategoryMutation,
    useDeleteParishBookCategoryMutation,
    useGetParishBookCategoriesQuery,
} from 'api/parishBookEndpoints';
import ParishBookCategoryEditModal from './ParishBookCategoryEditModal/ParishBookCategoryEditModal';
import useGetCurrentEntity from 'hooks/useGetCurrentEntity';
import parishBookMessages from '../../parishBookMessages';

const ParishBookCategoryTags = () => {
    const intl = useIntl();
    const currentEntityId = useGetCurrentEntityId();
    const currentEntity = useGetCurrentEntity();
    const isSubgroup = Boolean(currentEntity?.parentId);
    const { data, isFetching, isError, refetch } = useGetParishBookCategoriesQuery(
        currentEntityId ?? '',
        { skip: !currentEntityId },
    );
    const [onAddCategory, { isLoading: addLoading }] = useCreateParishBookCategoryMutation();
    const [onRemoveCategory, { isLoading: removeLoading }] = useDeleteParishBookCategoryMutation();
    const { isAdmin, isModerator, isOwner } = useGetUserPermission();

    const categories = useMemo(() => data ?? [], [data]);

    const [categoryToEdit, setCategoryToEdit] = useState<ParishBookCategoryDto | null>(null);

    const onAdd = useCallback(async (name: string) => {
        if (isSubgroup || !currentEntityId) return;
        await onAddCategory({ entityId: currentEntityId, data: { name } }).unwrap();
    }, [currentEntityId, isSubgroup, onAddCategory]);

    const onRemove = useCallback(async (category: InputElement | null) => {
        if (isSubgroup || !category?.id || !currentEntityId) return;
        try {
            await onRemoveCategory({ entityId: currentEntityId, id: category.id });
        } catch (e) {
            // handled in rtk query
        }
    }, [currentEntityId, isSubgroup, onRemoveCategory]);

    const onClick = useCallback((id?: string) => {
        if (isSubgroup) return;
        const category = categories.find((cat) => cat.id === id) ?? null;
        setCategoryToEdit(category);
    }, [categories, isSubgroup]);

    const onCategoryModalClose = useCallback(() => {
        setCategoryToEdit(null);
    }, []);
    const showEmptyState = categories.length === 0 && !isFetching;

    if (isError) {
        return <ErrorActionState className={styles.error} onClick={refetch} />;
    }

    return (
        <>
            <TagsInput
                textInputId="parish-book-category-tags-input"
                elements={categories}
                onAdd={onAdd}
                isDisabled={isSubgroup || (!isAdmin && !isModerator && !isOwner) || isFetching || addLoading || removeLoading}
                disabledTooltip={isSubgroup
                    ? intl.formatMessage(parishBookMessages.subgroupReadOnlyTooltip)
                    : undefined}
                onRemove={onRemove}
                onClick={onClick}
                messages={messages}
                maxLength={CONTACT_ROLE_MAX_LENGTH}
            />
            {showEmptyState && (
                <EmptyState
                    className={styles.emptyState}
                    title="Brak ról lub funkcji kontaktów"
                />
            )}
            <ParishBookCategoryEditModal
                open={!!categoryToEdit && !isSubgroup}
                onClose={onCategoryModalClose}
                category={categoryToEdit}
            />
        </>
    );
};

const messages = defineMessages({
    title: {
        id: 'parishBook.categories.title',
        defaultMessage: 'Nowa rola lub funkcja',
    },
    titlePlaceholder: {
        id: 'parishBook.categories.titlePlaceholder',
        defaultMessage: 'Wprowadź nazwę nowej roli lub funkcji',
    },
    elementAlreadyExist: {
        id: 'parishBook.categories.elementAlreadyExist',
        defaultMessage: 'Taka rola lub funkcja już istnieje. Dodaj inną.',
    },
    formName: {
        id: 'parishBook.categories.formName',
        defaultMessage: 'Formularz ról i funkcji kontaktów parafialnych',
    },
    elementTooLong: {
        id: 'parishBook.categories.elementTooLong',
        defaultMessage: 'Nazwa może mieć maksymalnie {max} znaków.',
    },
    elementAdded: {
        id: 'parishBook.categories.elementAdded',
        defaultMessage: 'Dodano rolę lub funkcję.',
    },
});

export default ParishBookCategoryTags;
